import { supabase } from '../lib/supabase';
import { createAPIMethod } from './base';
import { log } from '../utils/logger';

/**
 * Contact API module
 * Handles all contact form related API operations
 */

/**
 * Contact API namespace
 */
export const contactAPI = {
  /**
   * Submit a new contact form message (for authenticated users via edge function)
   * @param {Object} data - Contact form data
   * @param {string} data.name - Name of the person submitting
   * @param {string} data.email - Email address
   * @param {string} data.subject - Subject line
   * @param {string} data.message - Message content
   * @returns {Promise<APIResponse>}
   */
  submit: createAPIMethod(
    async (data) => {
      const { name, email, subject, message } = data;

      // Validate required fields
      if (!name || !email || !subject || !message) {
        throw new Error('All fields are required: name, email, subject, message');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please provide a valid email address');
      }

      // Get the current user to pass user_id to edge function
      const { data: { user } } = await supabase.auth.getUser();

      // Call the edge function for authenticated users (no reCAPTCHA token needed)
      const { data: functionData, error: functionError } = await supabase.functions.invoke('submit-contact-form', {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          subject: subject.trim(),
          message: message.trim(),
          user_id: user?.id // Pass user_id explicitly for authenticated users
          // No recaptchaToken for authenticated users
        }
      });

      if (functionError) {
        throw functionError;
      }

      // Return success
      return { data: functionData, error: null };
    },
    { operation: 'Submit contact form' }
  ),

  /**
   * Submit contact form for anonymous users with reCAPTCHA verification
   * @param {Object} data - Contact form data
   * @param {string} data.name - Name of the person submitting
   * @param {string} data.email - Email address
   * @param {string} data.subject - Subject line
   * @param {string} data.message - Message content
   * @param {string} data.recaptchaToken - reCAPTCHA token for verification
   * @returns {Promise<APIResponse>}
   */
  submitAnonymous: createAPIMethod(
    async (data) => {
      const { name, email, subject, message, recaptchaToken } = data;

      // Validate required fields
      if (!name || !email || !subject || !message) {
        throw new Error('All fields are required: name, email, subject, message');
      }

      if (!recaptchaToken) {
        throw new Error('reCAPTCHA verification is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please provide a valid email address');
      }

      // Get the current session (will be null for anonymous users)
      const { data: { session } } = await supabase.auth.getSession();

      // Call the edge function for proper reCAPTCHA verification
      const functionOptions = {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          subject: subject.trim(),
          message: message.trim(),
          recaptchaToken
        }
      };

      // Only add Authorization header if we have a valid session
      if (session?.access_token) {
        functionOptions.headers = {
          Authorization: `Bearer ${session.access_token}`
        };
      }

      const { data: functionData, error: functionError } = await supabase.functions.invoke('submit-contact-form', functionOptions);

      if (functionError) {
        throw functionError;
      }

      // Return success
      return { data: functionData, error: null };
    },
    { operation: 'Submit anonymous contact form' }
  ),

  /**
   * Get all contact messages (for TeamRezy employees)
   * @param {Object} filters - Filter options
   * @param {string} [filters.status] - Filter by status (unread, read, responded, archived)
   * @param {number} [filters.limit] - Limit number of results
   * @param {number} [filters.offset] - Offset for pagination
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('contact_us')
        .select(`
          *,
          user:user_id(
            id,
            first_name,
            last_name,
            email
          ),
          responded_by_user:responded_by(
            id,
            first_name,
            last_name,
            email
          )
        `);

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      // Add pagination
      if (filters.limit) {
        query = query.limit(filters.limit);
      }
      
      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);
      }

      // Order by created_at descending (newest first)
      query = query.order('created_at', { ascending: false });

      return await query;
    },
    { operation: 'Get contact messages' }
  ),

  /**
   * Get contact messages for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getByUser: createAPIMethod(
    async (userId) => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      return await supabase
        .from('contact_us')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
    },
    { operation: 'Get user contact messages' }
  ),

  /**
   * Update contact message status
   * @param {string} id - Contact message ID
   * @param {Object} updates - Fields to update
   * @param {string} [updates.status] - New status
   * @param {string} [updates.responded_by] - ID of user who responded
   * @returns {Promise<APIResponse>}
   */
  updateStatus: createAPIMethod(
    async (id, updates) => {
      if (!id) {
        throw new Error('Contact message ID is required');
      }

      const updateData = { ...updates };
      
      // If marking as responded, set responded_at timestamp
      if (updates.status === 'responded' && !updateData.responded_at) {
        updateData.responded_at = new Date().toISOString();
      }

      return await supabase
        .from('contact_us')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update contact message status' }
  ),

  /**
   * Delete a contact message
   * @param {string} id - Contact message ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      if (!id) {
        throw new Error('Contact message ID is required');
      }

      return await supabase
        .from('contact_us')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete contact message' }
  ),

  /**
   * Get contact message by ID
   * @param {string} id - Contact message ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      if (!id) {
        throw new Error('Contact message ID is required');
      }

      return await supabase
        .from('contact_us')
        .select(`
          *,
          user:user_id(
            id,
            first_name,
            last_name,
            email
          ),
          responded_by_user:responded_by(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('id', id)
        .single();
    },
    { operation: 'Get contact message' }
  ),

  /**
   * Get contact statistics (for admin dashboard)
   * @returns {Promise<APIResponse>}
   */
  getStats: createAPIMethod(
    async () => {
      // Get count by status
      const { data: statusCounts, error: statusError } = await supabase
        .from('contact_us')
        .select('status')
        .then(result => {
          if (result.error) return result;

          const counts = {
            total: result.data.length,
            unread: 0,
            read: 0,
            responded: 0,
            archived: 0
          };

          result.data.forEach(row => {
            counts[row.status] = (counts[row.status] || 0) + 1;
          });

          return { data: counts, error: null };
        });

      if (statusError) {
        throw statusError;
      }

      // Get recent activity (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: recentCount, error: recentError } = await supabase
        .from('contact_us')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (recentError) {
        log.warn('Failed to get recent contact count:', recentError);
      }

      return {
        ...statusCounts,
        recent_30_days: recentCount || 0
      };
    },
    { operation: 'Get contact statistics' }
  ),

  /**
   * Submit a data deletion request (for authenticated users)
   * @param {Object} data - Deletion request data
   * @param {string} data.name - Name of the person requesting deletion
   * @param {string} data.email - Email address
   * @param {string} data.reason - Reason for deletion
   * @param {string} data.additionalInfo - Additional information (optional)
   * @returns {Promise<APIResponse>}
   */
  submitDeletionRequest: createAPIMethod(
    async (data) => {
      const { name, email, reason, additionalInfo } = data;

      // Validate required fields
      if (!name || !email || !reason) {
        throw new Error('Required fields: name, email, reason');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please provide a valid email address');
      }

      // Get the current user to pass user_id to edge function
      const { data: { user } } = await supabase.auth.getUser();

      // Call the edge function for authenticated users (no reCAPTCHA token needed)
      const { data: functionData, error: functionError } = await supabase.functions.invoke('submit-deletion-request', {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          reason: reason.trim(),
          additional_info: additionalInfo?.trim() || '',
          user_id: user?.id // Pass user_id explicitly for authenticated users
        }
      });

      if (functionError) {
        throw functionError;
      }

      // Return success
      return { data: functionData, error: null };
    },
    { operation: 'Submit deletion request' }
  ),

  /**
   * Submit data deletion request for anonymous users with reCAPTCHA verification
   * @param {Object} data - Deletion request data
   * @param {string} data.name - Name of the person requesting deletion
   * @param {string} data.email - Email address
   * @param {string} data.reason - Reason for deletion
   * @param {string} data.additionalInfo - Additional information (optional)
   * @param {string} data.recaptchaToken - reCAPTCHA token for verification
   * @returns {Promise<APIResponse>}
   */
  submitDeletionRequestAnonymous: createAPIMethod(
    async (data) => {
      const { name, email, reason, additionalInfo, recaptchaToken } = data;

      // Validate required fields
      if (!name || !email || !reason) {
        throw new Error('Required fields: name, email, reason');
      }

      if (!recaptchaToken) {
        throw new Error('reCAPTCHA verification is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please provide a valid email address');
      }

      // Get the current session (will be null for anonymous users)
      const { data: { session } } = await supabase.auth.getSession();

      // Call the edge function for proper reCAPTCHA verification
      const functionOptions = {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          reason: reason.trim(),
          additional_info: additionalInfo?.trim() || '',
          recaptchaToken
        }
      };

      // Only add Authorization header if we have a valid session
      if (session?.access_token) {
        functionOptions.headers = {
          Authorization: `Bearer ${session.access_token}`
        };
      }

      const { data: functionData, error: functionError } = await supabase.functions.invoke('submit-deletion-request', functionOptions);

      if (functionError) {
        throw functionError;
      }

      // Return success
      return { data: functionData, error: null };
    },
    { operation: 'Submit anonymous deletion request' }
  ),

  /**
   * Get all data deletion requests (TeamRezy employees only)
   * @returns {Promise<APIResponse>}
   */
  getDeletionRequests: createAPIMethod(
    async () => {
      const { data, error } = await supabase
        .from('data_deletion_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data;
    },
    { operation: 'Get deletion requests' }
  ),

  /**
   * Get deletion requests statistics (TeamRezy employees only)
   * @returns {Promise<APIResponse>}
   */
  getDeletionRequestsStats: createAPIMethod(
    async () => {
      const { data, error } = await supabase
        .from('data_deletion_requests')
        .select('status');

      if (error) {
        throw error;
      }

      // Count by status
      const stats = {
        total: data.length,
        pending: data.filter(r => r.status === 'pending').length,
        in_review: data.filter(r => r.status === 'in_review').length,
        approved: data.filter(r => r.status === 'approved').length,
        completed: data.filter(r => r.status === 'completed').length,
        cancelled: data.filter(r => r.status === 'cancelled').length,
      };

      return stats;
    },
    { operation: 'Get deletion requests statistics' }
  ),

  /**
   * Update deletion request status (TeamRezy employees only)
   * @param {string} requestId - Request ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<APIResponse>}
   */
  updateDeletionRequest: createAPIMethod(
    async (requestId, updates) => {
      const { data, error} = await supabase
        .from('data_deletion_requests')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    { operation: 'Update deletion request' }
  )
};